import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { FC, useEffect, useMemo, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap";
import { formatNumber, getISO2ByISO3, getNameByISO3 } from "../../../../libs/misc";
import DataTable from "../../../../../ui-kit/DataTable";

type DetailProductModalProps = {
  show: boolean;
  data: any;
  onToggle: (data:any) => void;
};

interface DataRow {
  rep: string;
  hs:string;
  tariff: number;
  import_from_khm: number;
  total:number
}

const columnHelper = createColumnHelper<DataRow>();

const DetailProductModal: FC<DetailProductModalProps> = ({data, show, onToggle}) => {
  const [dataTable, setDataTable] = useState<DataRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [includeZero, setIncludeZero] = useState(false);
  const hsCode = data?.hs || '';

  

  const columns: ColumnDef<DataRow, any>[] = useMemo(() => {
    return [
    columnHelper.accessor('rep', {
      header: 'Economy',
      cell: info => (
      <>
        <span className={`fi fi-${getISO2ByISO3(info.getValue())?.toLowerCase() || ''} me-2`}></span> 
        {getNameByISO3(info.getValue())}
      </>
      ),
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-left font-semibold text-dark',
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('hs', {
      header: 'Commodity',
      cell: info => (
        <span
        className="badge text-primary fw-1000"
        style={{ background: 'rgba(108, 117, 125, 0.1)' }}
          >
        {info.getValue()}
        </span>
      ),
      meta: {
        headerClass: 'text-center',
        cellClass: 'text-center font-semibold text-dark',
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('tariff', {
      header: 'Tariff',
      cell: info => `${Math.round(info.getValue())}%`,
      meta: {
        cellClass: 'text-end',
        headerClass: 'font-bold'
      },
      enableSorting: true,
      enableGlobalFilter: true,
    }),
    columnHelper.accessor('import_from_khm', {
      header: 'Import from KHM',
      cell: info => formatNumber(Math.round(info.getValue())),
      meta: {
        cellClass: 'text-end',
        headerClass: "font-bold"
      },
      enableSorting: true,
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('total', {
      header: 'Total Potential Change',
      cell: info => {
        const value = Math.round(info.getValue());
        if(value>0){
          return (<span className="text-success fw-bolder">+{formatNumber(value)}</span>)
        }else if(value<0){
          return (<span className="text-danger fw-bolder">-{formatNumber(value)}</span>)
        }
        return (<span className="fw-bolder">{formatNumber(value)}</span>)
      },
      meta: {
        headerClass: 'font-bold',
        cellClass: 'text-end',
      },
      filterFn: (row, columnId, filterValue) => {
        const value = row.getValue<number>(columnId);
        return filterValue === true // includeZero
          ? true                     // show everything
          : value !== 0;             // exclude zero values      
      },    
      enableSorting: true,
      enableGlobalFilter: false,
    }),
  ]}, [hsCode, includeZero]); // Include hsCode and includeZero in dependencies

  if(hsCode.length === 6) {
    columns.splice(1, 1)
  }  
  
  useEffect(() => {
    if(data?.data && data?.data.length > 0){
      setIsLoading(true);
      const formattedData = data.data.map(item => ({
        rep: item.rep,
        hs: item.hs,
        tariff: item.tariff,
        import_from_khm: item.imports,
        total: item.tot
      }));

      setDataTable(formattedData);
      setIsLoading(false);
    } else {
      setDataTable([]);
      setIsLoading(false);
    }

    if(hsCode.length!==6){
      columns.splice(1,0,
        )  
    }
  },[data])

  return (
    <Modal 
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered 
      show={show} 
      onHide={() => onToggle(null)}>
      <Modal.Header closeButton>
        <Modal.Title>Countries Imposing Tariffs on Cambodian Exports of {hsCode}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Check
          type="switch"
          id="dpm-zero-toggle"
          label="Include total potential change of zero"
          checked={includeZero}
          onChange={(e) => setIncludeZero(e.target.checked)}
          className="form-switch form-switch-sm mb-3"
          style={{ minHeight: "1.5rem" }}
        />

        <DataTable<DataRow>
          data={dataTable}
          columns={columns}
          isLoading={isLoading}
          initialColumnFilters={[
            {
              id: 'total',
              value: includeZero 
            }
          ]}      
          enablePagination={true}
          enableSorting={true}
          enableGlobalFilter={true}
          globalFilterPlaceholder=""
          defaultPageSize={10}
          pageSizes={[5, 10, 20, 50, 100]}
          className="my-custom-table-wrapper"
          initialSortBy={{ id: 'import_from_khm', desc: true }} 
        />
      </Modal.Body>
      <Modal.Footer>
        <span className="text-muted fst-italic">Disclaimer: The following data represents import records reported by each trading partner (mirror data), capturing Cambodian-origin products subject to tariffs. This may differ from Cambodia’s own reported export data and may not necessarily reflect its top export destinations. To explore Cambodia’s official export data, including top export markets, please click 
          <a
            href={`/trade-prospect-commodity/${hsCode.length === 6 ? hsCode : (data?.data && data.data.length > 0 ? data.data[0].hs : '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="fw-1000 ms-1"
          >
            here
          </a>
        </span>
        <Button variant="primary" onClick={onToggle}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

}

export {DetailProductModal}